import React from "react";
import { Award, Users, Leaf, Target, Quote } from "lucide-react";

const DIRECTOR_IMG = "https://images.unsplash.com/photo-1614890085618-0e1054da74f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBibGFjayUyMG1hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4OTAzNzkyM3ww&ixlib=rb-4.1.0&q=85";

const values = [
  { icon: Target, title: "Rigueur", desc: "Chaque intervention suit un protocole strict, vérifié par un chef d'équipe." },
  { icon: Leaf, title: "Éco-responsabilité", desc: "Produits certifiés Ecolabel, gestion optimisée de l'eau et des déchets." },
  { icon: Users, title: "Humain", desc: "Nos agents sont formés, respectés et fidélisés — c'est notre force." },
  { icon: Award, title: "Exigence", desc: "Un contrôle qualité mensuel garantit la constance de nos prestations." },
];

const milestones = [
  { year: "2015", title: "Création", desc: "Sindou Kamagate fonde GIGA CLEAN SERVICE avec une vision claire : réinventer le nettoyage professionnel." },
  { year: "2018", title: "Expansion", desc: "L'entreprise s'implante durablement en région parisienne et étoffe ses équipes." },
  { year: "2021", title: "Certification qualité", desc: "Adoption des normes AFNOR et engagement dans une démarche RSE ambitieuse." },
  { year: "2024", title: "Nouveaux services", desc: "Lancement des prestations vitrerie en hauteur et remise en état complète." },
];

export default function About() {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="pt-20 md:pt-28 pb-16">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-4">À propos</div>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Une entreprise à taille humaine, une exigence de grande maison.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Depuis 2015, GIGA CLEAN SERVICE accompagne les entreprises d'Île-de-France dans l'entretien
              quotidien de leurs espaces. Notre force ? Des équipes stables, formées, et une relation
              client de proximité, portée par un dirigeant présent sur le terrain.
            </p>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl shadow-[#2196F3]/20">
              <img src={DIRECTOR_IMG} alt="Sindou Kamagate, Directeur de GIGA CLEAN SERVICE" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 left-6 right-6 bg-white border border-slate-100 rounded-2xl p-5 shadow-xl">
              <div className="text-xs uppercase tracking-[0.2em] text-[#2196F3] font-semibold">Directeur</div>
              <div className="font-display font-bold text-xl text-slate-900 mt-1">Sindou Kamagate</div>
              <div className="text-sm text-slate-500">Fondateur — GIGA CLEAN SERVICE</div>
            </div>
          </div>
        </div>
      </section>

      {/* Director quote */}
      <section className="py-20 bg-[#f0f7ff]">
        <div className="mx-auto max-w-4xl px-6 md:px-10 text-center">
          <Quote className="h-10 w-10 text-[#2196F3] mx-auto mb-6" />
          <blockquote className="font-display text-2xl md:text-3xl font-semibold text-slate-800 leading-snug">
            « Notre métier, c'est le soin. Soin des lieux, soin des équipes, soin de la relation
            avec nos clients. Chaque contrat que nous signons est une promesse tenue jour après jour. »
          </blockquote>
          <div className="mt-8 text-sm text-slate-500">— Sindou Kamagate, Directeur</div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-4">Nos valeurs</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900">
              Quatre principes, un même engagement.
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {values.map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-2xl p-8 hover:border-[#2196F3]/40" style={{ transitionProperty: "border-color", transitionDuration: "300ms" }}>
                <div className="h-12 w-12 rounded-xl bg-[#E3F2FD] flex items-center justify-center mb-5">
                  <Icon className="h-6 w-6 text-[#2196F3]" />
                </div>
                <div className="font-display font-bold text-xl text-slate-900">{title}</div>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-5xl px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-4">Notre histoire</div>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-14">
            10 ans de croissance et de confiance.
          </h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-slate-200" aria-hidden />
            <div className="space-y-14">
              {milestones.map((m, i) => (
                <div key={i} className={`relative flex md:items-center gap-6 md:gap-12 ${i % 2 === 1 ? "md:flex-row-reverse" : ""}`}>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 h-4 w-4 rounded-full bg-[#2196F3] border-4 border-white ring-2 ring-[#2196F3]/30" aria-hidden />
                  <div className="md:w-1/2 pl-12 md:pl-0 md:px-8">
                    <div className="font-display text-4xl font-extrabold text-[#2196F3]">{m.year}</div>
                    <div className="font-display font-bold text-xl text-slate-900 mt-2">{m.title}</div>
                    <p className="text-slate-600 mt-2 leading-relaxed">{m.desc}</p>
                  </div>
                  <div className="hidden md:block md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
